import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { Toast } from '@components/Toast/Toast';
import { VideoSkeleton } from '@components/VideoSkeleton/VideoSkeleton';
import CloseIcon from '@mui/icons-material/Close';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Divider, IconButton, Modal, Skeleton, Tooltip } from '@mui/material';
import { billingStore } from '@stores/Billing';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useModalRegistration } from '@stores/ModalStore';
import { useMomentMergeStore } from '@stores/MomentMerge/MomentMergeStore';
import { trackingStore } from '@stores/Tracking';
import { useRemoveWatermark } from 'hooks/useRemoveWatermark';
import { observer } from 'mobx-react';
import {
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { MomentMergeTimeline } from './components/MomentList/MomentMergeTimeline';
import { MomentMergeVideoPlayer } from './components/MomentMergeVideoPlayer/MomentMergeVideoPlayer';
import { MomentMergeVideoPlayerRef } from './components/MomentMergeVideoPlayer/types';
import css from './MomentMergeModal.module.scss';
import { MomentTrimModal, MomentTrimModalRef } from './MomentTrimModal';

export type MomentMergeModalRef = {
  open: () => void;
  close: () => void;
};

type MomentMergeModalProps = {};

export const MomentMergeModal = observer(
  forwardRef<MomentMergeModalRef, MomentMergeModalProps>(({}, ref) => {
    const libraryManager = useLibraryManager();
    const videoPlayerRef = useRef<MomentMergeVideoPlayerRef>(null);
    const momentTrimModalRef = useRef<MomentTrimModalRef>(null);
    const [open, setOpen] = useState(false);
    const [format, setFormat] = useState<'mp4'>('mp4');
    const [isLoading, setIsLoading] = useState(true);
    const [trimmingMomentId, setTrimmingMomentId] = useState<string | null>(null);
    const momentMergeStore = useMomentMergeStore();
    const downloadedRef = useRef(false);

    // Calculate total seconds for watermark removal cost
    const totalSeconds = momentMergeStore.clips.reduce((total, clip) => total + clip.duration, 0);
    const {
      removeWatermark,
      setRemoveWatermark,
      watermarkRemovalCost,
      isWatermarkEnabled,
      watermarkRemovalTooltipText,
    } = useRemoveWatermark({
      totalSeconds,
    });

    const momentSelection = libraryManager.multiLibSearch.momentSelection;
    const enoughMomentsSelected = (() => {
      return momentSelection.selectedMoments.size >= 2;
    })();

    // Determine the active moment ID based on current time
    const activeMomentId = useMemo(() => {
      if (momentMergeStore.clips.length === 0) return null;

      const clipInfo = momentMergeStore.clipInTimestamp(momentMergeStore.currentTime);
      if (!clipInfo || clipInfo.currentClipIndex >= momentMergeStore.clips.length) return null;

      return momentMergeStore.clips[clipInfo.currentClipIndex].id;
    }, [momentMergeStore.currentTime, momentMergeStore.clips]);

    // Effect to open the trim modal after it's rendered
    useEffect(() => {
      if (trimmingMomentId && momentTrimModalRef.current) {
        momentTrimModalRef.current.open();
      }
    }, [trimmingMomentId]);

    useModalRegistration('moment-merge', open);

    const onOpen = () => {
      if (open) return;

      setOpen(true);
      trackingStore.momentMergeModalOpened({ clipCount: momentMergeStore.clips.length });
    };

    const onClose = (event?: any, reason?: string) => {
      if (event) {
        event.stopPropagation();
      }

      if (!downloadedRef.current) {
        trackingStore.momentMergeModalCancelled({ clipCount: momentMergeStore.clips.length });
      }
      downloadedRef.current = false;
      setOpen(false);
      setIsLoading(true);
    };

    const handleDownload = async () => {
      let toastId: string | number | undefined;
      try {
        toastId = toast.custom(
          (t) => (
            <Toast
              id={t}
              header={`Downloading moments as ${format === 'mp4' ? 'MP4' : format === 'gif' ? 'GIF' : 'MP4 and GIF'}`}
              description="Please wait a bit."
              variant="success"
            />
          ),
          { duration: Infinity },
        );

        const promises = [
          momentMergeStore.renderClips({
            filename: libraryManager.multiLibSearch.request?.text ?? 'mixed_moments',
            extension: format === 'mp4' ? 'mp4' : 'gif',
            watermark: isWatermarkEnabled,
          }),
        ];

        await Promise.all(promises);

        downloadedRef.current = true;

        trackingStore.momentMergeDownloaded({
          clipCount: momentMergeStore.clips.length,
          format,
          removeWatermark: removeWatermark === 'yes',
          totalDurationSeconds: totalSeconds,
        });

        // Refetch credits after successful download
        await billingStore.refetchCreditsAfterOperation();
      } catch (error) {
        toast.custom(
          (t) => (
            <Toast id={t} header="Error" description="Failed to download moment" variant="error" />
          ),
          { duration: 3000 },
        );
      }
      if (toastId) {
        toast.dismiss(toastId);
      }
    };

    const handleTrimExpand = (momentId: string) => {
      // Pause the video when opening the trim modal
      if (videoPlayerRef.current) {
        const video = videoPlayerRef.current.video;
        if (video) {
          video.pause();
        }
      }
      setTrimmingMomentId(momentId);
    };

    const handleBackToMerging = () => {
      setTrimmingMomentId(null);
      // The video player will automatically detect clip changes and reinitialize
      // when the momentMergeStore.clips are updated by the trim modal.
      // The useEffect in MomentMergeVideoPlayer will handle finding the correct
      // active clip and time based on the current virtual timestamp.
    };

    const handleMomentDoubleClick = (momentId: string) => {
      // Find the moment in the clips array
      const momentIndex = momentMergeStore.clips.findIndex((clip) => clip.id === momentId);
      if (momentIndex === -1) return;

      // Calculate the start time of this moment in the virtual timeline
      const previousClipsDuration = momentMergeStore.clips
        .slice(0, momentIndex)
        .reduce((total, clip) => total + clip.duration, 0);

      // Get the current moment's duration
      const currentMoment = momentMergeStore.clips[momentIndex];
      const momentDuration = currentMoment.duration;

      // Seek to 0.01s into the clip if it's longer than 0.01s, otherwise seek to the start
      const seekOffset = momentDuration > 0.01 ? 0.01 : 0;
      const seekTime = previousClipsDuration + seekOffset;

      // Seek the video player to the start of this moment
      if (videoPlayerRef.current) {
        videoPlayerRef.current.scrubTo(seekTime);
      }
    };

    const handleMomentReordered = useCallback(
      (momentId: string) => {
        trackingStore.momentReordered();
        // Find the moment in the clips array after reordering
        const momentIndex = momentMergeStore.clips.findIndex((clip) => clip.id === momentId);
        if (momentIndex === -1) return;

        // Calculate the start time of this moment in the virtual timeline
        const previousClipsDuration = momentMergeStore.clips
          .slice(0, momentIndex)
          .reduce((total, clip) => total + clip.duration, 0);

        // Get the current moment's duration
        const currentMoment = momentMergeStore.clips[momentIndex];
        const momentDuration = currentMoment.duration;

        // Seek to 0.01s into the clip if it's longer than 0.01s, otherwise seek to the start
        const seekOffset = momentDuration > 0.01 ? 0.01 : 0;
        const seekTime = previousClipsDuration + seekOffset;

        // Seek the video player to the start of the reordered moment
        if (videoPlayerRef.current) {
          videoPlayerRef.current.scrubTo(seekTime);
        }
      },
      [momentMergeStore],
    );

    const handleMenuOpen = useCallback(() => {
      setTrimmingMomentId(null);
      // Pause the video player when a menu is opened
      if (videoPlayerRef.current && momentMergeStore.isPlaying) {
        videoPlayerRef.current.pause();
      }
    }, [momentMergeStore.isPlaying]);

    const handleContentClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    };

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    return (
      <>
        <Modal open={open} onClose={onClose} keepMounted={false}>
          <div className={css.momentMergeModal} onClick={handleContentClick}>
            <div className={css.upperRightCorner}>
              <IconButton onClick={onClose} size="small" sx={{ marginLeft: 'auto' }}>
                <CloseIcon />
              </IconButton>
            </div>
            <div className={css.header}>
              <span className={css.title}>Merge moments</span>
              <span className={css.subtitle}>
                Merge the moments below into a single video sequence. Reorder them freely using drag
                and drop.
              </span>
            </div>
            <div className={css.preview}>
              {isLoading && <VideoSkeleton className={css.skeletonVideo} />}
              <MomentMergeVideoPlayer
                ref={videoPlayerRef}
                hidden={isLoading}
                onReady={() => setIsLoading(false)}
                containerClassName={css.videoContainer}
                clipStore={momentMergeStore}
              />
            </div>
            <Divider />
            {isLoading ? (
              <Skeleton variant="rectangular" className={css.skeletonMomentList} animation="wave" />
            ) : (
              enoughMomentsSelected && (
                <MomentMergeTimeline
                  onTrimExpand={handleTrimExpand}
                  activeMomentId={activeMomentId}
                  onMomentDoubleClick={handleMomentDoubleClick}
                  onMomentReordered={handleMomentReordered}
                  onMenuOpen={handleMenuOpen}
                />
              )
            )}
            <Divider />
            <div className={css.footer}>
              <div className={css.optionsGrid}>
                <div className={css.gridHeader}>
                  <span className={css.formatLabel}>
                    <span className={css.comingSoon}>GIF compilations coming soon!</span>
                  </span>
                  <div className={css.watermarkLabelContainer}>
                    <span className={css.watermarkLabel}>Remove StarZero watermark</span>
                    <Tooltip title={watermarkRemovalTooltipText} arrow placement="top">
                      <InfoRoundedIcon className={css.infoIcon} />
                    </Tooltip>
                  </div>
                </div>
                <div className={css.gridControls}>
                  <MultipleToggle
                    options={[{ value: 'mp4', label: 'MP4' }]}
                    value={format}
                    onChange={setFormat}
                  />
                  <MultipleToggle
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    value={removeWatermark}
                    onChange={setRemoveWatermark}
                  />
                </div>
              </div>
              <button className={css.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button
                className={css.downloadButton}
                onClick={handleDownload}
                disabled={libraryManager.renderer.isRendering}
              >
                Download
              </button>
            </div>
          </div>
        </Modal>
        {trimmingMomentId && (
          <MomentTrimModal
            ref={momentTrimModalRef}
            momentId={trimmingMomentId}
            onBackToMerging={handleBackToMerging}
          />
        )}
      </>
    );
  }),
);
